interface AspectMetadataSource {
  metadata?: Record<string, unknown> | null;
}

function asPositiveNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function parseRatioToken(value: unknown): { width: number; height: number } | null {
  const token = asNonEmptyString(value);
  if (!token) return null;
  const match = token.match(/^(\d+(?:\.\d+)?)\s*[:x×]\s*(\d+(?:\.\d+)?)$/i);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? { width, height } : null;
}

const RATIO_PARAM_KEYS = ["ratio", "aspect_ratio", "image_size", "resolution"] as const;

const MIN_BOX_RATIO = 0.42;
const MAX_BOX_RATIO = 2.4;

function boxRatio(ratio: number): string {
  const clamped = Math.min(Math.max(ratio, MIN_BOX_RATIO), MAX_BOX_RATIO);
  return String(Number(clamped.toFixed(4)));
}

export function aspectRatioForJobAsset(
  asset: AspectMetadataSource | null | undefined,
  params: Record<string, unknown> | null | undefined
): string | null {
  const metadata = asset?.metadata ?? undefined;
  const width = asPositiveNumber(metadata?.width);
  const height = asPositiveNumber(metadata?.height);
  if (width && height) return boxRatio(width / height);

  const ratio = asPositiveNumber(metadata?.aspect_ratio);
  if (ratio) return boxRatio(ratio);

  for (const key of RATIO_PARAM_KEYS) {
    const parsed = parseRatioToken(params?.[key]);
    if (parsed) return boxRatio(parsed.width / parsed.height);
  }
  return null;
}

const CANONICAL_RATIOS: readonly (readonly [number, number])[] = [
  [21, 9], [2, 1], [16, 9], [16, 10], [5, 3], [3, 2], [4, 3], [5, 4], [1, 1],
  [4, 5], [3, 4], [2, 3], [10, 16], [9, 16], [9, 21],
];

const CANONICAL_TOLERANCE = 0.02;

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

function canonicalName(ratio: number): string | null {
  for (const [width, height] of CANONICAL_RATIOS) {
    const candidate = width / height;
    if (Math.abs(ratio - candidate) / candidate < CANONICAL_TOLERANCE) return `${width}:${height}`;
  }
  return null;
}

function ratioName(width: number, height: number): string {
  const canonical = canonicalName(width / height);
  if (canonical) return canonical;
  if (Number.isInteger(width) && Number.isInteger(height)) {
    const divisor = greatestCommonDivisor(width, height);
    const reducedWidth = width / divisor;
    const reducedHeight = height / divisor;
    if (reducedWidth <= 32 && reducedHeight <= 32) return `${reducedWidth}:${reducedHeight}`;
  }
  return `${(width / height).toFixed(2)}:1`;
}

export function aspectBadgeLabel(
  asset: AspectMetadataSource | null | undefined,
  params: Record<string, unknown> | null | undefined
): string | null {
  const metadata = asset?.metadata ?? undefined;
  const width = asPositiveNumber(metadata?.width);
  const height = asPositiveNumber(metadata?.height);
  if (width && height) return ratioName(width, height);

  const ratio = asPositiveNumber(metadata?.aspect_ratio);
  if (ratio) return canonicalName(ratio) ?? `${ratio.toFixed(2)}:1`;

  for (const key of RATIO_PARAM_KEYS) {
    const parsed = parseRatioToken(params?.[key]);
    if (parsed) return ratioName(parsed.width, parsed.height);
  }
  return null;
}

export function aspectRatioLabel(
  asset: AspectMetadataSource | null | undefined,
  params: Record<string, unknown> | null | undefined
): string | null {
  const token = asNonEmptyString(params?.ratio) ?? asNonEmptyString(params?.aspect_ratio);
  if (token) return token;

  const metadata = asset?.metadata ?? undefined;
  const ratio = asPositiveNumber(metadata?.aspect_ratio);
  if (ratio) return ratio.toFixed(2);

  const width = asPositiveNumber(metadata?.width);
  const height = asPositiveNumber(metadata?.height);
  if (width && height) return (width / height).toFixed(2);

  return null;
}
